import React, { useState, useEffect } from 'react';
import { AppShellSkeleton } from '@/components/layout/AppShellSkeleton';
import { bootStateMachine } from '@/lib/boot/BootStateMachine';
import { isTerminalState, isAuthReadyOrBeyond, onAuthReadyOrBeyond, logTerminalCancellation } from '@/lib/boot/terminalStateUtils';

interface StoreHydrationGateProps {
  children: React.ReactNode;
  timeout?: number;
}

/**
 * Self-healing function to ensure STORAGE_OK transition
 * Prevents race condition where HYDRATED is dispatched from INIT phase
 */
const ensureStorageOk = () => {
  const currentPhase = bootStateMachine.getPhase();
  if (currentPhase === 'INIT' && bootStateMachine.canTransition('STORAGE_OK')) {
    console.log('[HYDRATION_GATE] Auto-repair: transitioning INIT → STORAGE_OK');
    bootStateMachine.transition('STORAGE_OK');
  }
};

/**
 * Unified store hydration management
 * Uses persist API events for precise hydration tracking
 */
export const StoreHydrationGate: React.FC<StoreHydrationGateProps> = ({ 
  children, 
  timeout = 1200 // Reduced from 1800ms for faster boot
}) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [hydrationError, setHydrationError] = useState<string | null>(null);
  const errorLogged = React.useRef(false);
  const didSetHydratedRef = React.useRef(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let hydrationPromises: Promise<void>[] = [];
    let unsubscribeTerminal: (() => void) | null = null;

    // Check if already terminal on mount
    if (isTerminalState()) {
      console.log('[HYDRATION_GATE] Already terminal on mount, setting hydrated immediately');
      if (!didSetHydratedRef.current) {
        didSetHydratedRef.current = true;
        setIsHydrated(true);
      }
      return;
    }

    const hydrateStores = async () => {
      try {
        // Dynamic imports with error handling for each store
        const storeModules = await Promise.allSettled([
          import('@/stores/clientDataStore').then(m => ({ name: 'clientDataStore', module: m })),
          import('@/stores/unifiedDebtStore').then(m => ({ name: 'unifiedDebtStore', module: m }))
        ]);

        const availableStores: string[] = [];
        const hydratedStores: string[] = [];
        hydrationPromises = [];

        // Process each store import result
        storeModules.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            const { name, module } = result.value;
            availableStores.push(name);

            // Create hydration promise for this store
            hydrationPromises.push(
              new Promise<void>((resolve, reject) => {
                try {
                  const storeHook = name === 'clientDataStore' 
                    ? (module as any).useClientDataStore 
                    : (module as any).useUnifiedDebtStore;
                  
                  // Check if store has persist capability
                  if ((storeHook as any).persist) {
                    // Use official Zustand persist signals
                    if ((storeHook as any).persist.hasHydrated()) {
                      // Already hydrated
                      hydratedStores.push(name);
                      resolve();
                    } else {
                      // Set up hydration listener
                      const unsubscribe = (storeHook as any).persist.onFinishHydration(() => {
                        unsubscribe();
                        hydratedStores.push(name);
                        resolve();
                      });
                      
                      // Trigger rehydration if needed
                      (storeHook as any).persist.rehydrate();
                    }
                  } else {
                    // Non-persisted store - immediately resolve
                    hydratedStores.push(name);
                    resolve();
                  }
                } catch (error) {
                  console.warn(`${name} hydration setup failed:`, error);
                  // Don't reject - proceed without this store
                  resolve();
                }
              })
            );
          } else {
            const storeName = index === 0 ? 'clientDataStore' : 'unifiedDebtStore';
            console.warn(`Failed to import ${storeName}:`, result.reason);
            // Continue without this store - it will use defaults
          }
        });

        // Wait for all available stores to hydrate with timeout
        if (hydrationPromises.length > 0) {
          await Promise.all(hydrationPromises);
        }
        
        console.log('✅ [BOOT_STEP_OK]', {
          step: 'store_hydration',
          ts: Date.now(),
          availableStores,
          hydratedStores,
          total: availableStores.length,
          safeMode: false,
          hasStores: availableStores.length > 0
        });
        
        // Update boot state machine (with self-healing check and guard)
        ensureStorageOk();
        if (bootStateMachine.canTransition('HYDRATED')) {
          bootStateMachine.transition('HYDRATED');
        }
        
        if (!didSetHydratedRef.current) {
          didSetHydratedRef.current = true;
          setIsHydrated(true);
        }
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown hydration error';
        
        // Log error only once
        if (!errorLogged.current) {
          console.error('Store hydration failed (best-effort continues):', errorMessage);
          errorLogged.current = true;
        }
        
        setHydrationError(errorMessage);
        
        // Still set as hydrated to prevent infinite loading - best effort approach
        if (!didSetHydratedRef.current) {
          didSetHydratedRef.current = true;
          setIsHydrated(true);
        }
        
        console.log('✅ [BOOT_STEP_OK]', {
          step: 'store_hydration',
          ts: Date.now(),
          error: errorMessage,
          safeMode: false,
          hasStores: false,
          fallback: true
        });
      }
    };

    // 🔥 MODIFIED: Reduce timeout for background tabs
    const wasHiddenOnMount = document.hidden;
    const effectiveTimeout = wasHiddenOnMount ? 800 : timeout; // Faster for background

    // Set timeout fallback (one-shot) with terminal guard
    timeoutId = setTimeout(() => {
      // Guard: Don't execute timeout if already terminal
      if (isTerminalState()) {
        console.log('[HYDRATION_GATE] Timeout fired but already terminal, skipping');
        return;
      }

      console.warn('Store hydration timeout reached, proceeding with default states');
      console.log('✅ [BOOT_STEP_OK]', {
        step: 'store_hydration',
        ts: Date.now(),
        timeout: true,
        wasHidden: wasHiddenOnMount,
        safeMode: false,
        hasStores: false,
        fallback: true
      });
      
      // Update boot state machine (one-shot with self-healing check)
      ensureStorageOk();
      if (bootStateMachine.canTransition('HYDRATION_TIMEOUT')) {
        bootStateMachine.transition('HYDRATION_TIMEOUT');
      }
      
      if (!didSetHydratedRef.current) {
        didSetHydratedRef.current = true;
        setIsHydrated(true);
      }
    }, effectiveTimeout);

    // Subscribe to terminal state changes to cancel timeout and set hydrated
    unsubscribeTerminal = onAuthReadyOrBeyond((phase) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        logTerminalCancellation('hydration_gate', 'hydration timeout', phase);
      }
      
      if (!didSetHydratedRef.current) {
        didSetHydratedRef.current = true;
        setIsHydrated(true);
      }
    });
    
    // Lightweight visibility listener - only dispatches resize when terminal
    const handleVisibilityForTerminal = () => {
      if (document.visibilityState === 'visible' && isTerminalState()) {
        console.log('[HYDRATION_GATE] visible→resize dispatched (terminal)');
        window.dispatchEvent(new Event('resize'));
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityForTerminal);

    // Start hydration
    hydrateStores();

    return () => {
      clearTimeout(timeoutId);
      unsubscribeTerminal?.();
      document.removeEventListener('visibilitychange', handleVisibilityForTerminal);
    };
  }, [timeout]);

  // 🎯 NEW: Immediate render trigger for foreground tabs (terminal state listener)
  useEffect(() => {
    let hasTriggered = false;
    
    const triggerInitialRender = (phase: string) => {
      if (hasTriggered || document.visibilityState !== 'visible') return;
      
      hasTriggered = true;
      console.log('[HYDRATION_GATE] Initial render trigger (foreground load)', { phase });
      
      // Use double requestAnimationFrame for reliable timing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
          void document.body.offsetHeight; // Force reflow
          
          // 🔥 Force CSS visibility to override any hiding styles
          const selectors = [
            'main',
            '[data-testid="dashboard"]',
            '[data-testid="app-content"]',
            '[role="main"]',
            '.dashboard-content',
            '#app-content'
          ];
          
          const foundElements: string[] = [];
          selectors.forEach(selector => {
            const element = document.querySelector(selector);
            if (element && element instanceof HTMLElement) {
              element.style.setProperty('opacity', '1', 'important');
              element.style.setProperty('visibility', 'visible', 'important');
              element.style.setProperty('display', 'block', 'important');
              element.style.setProperty('transform', 'none', 'important');
              element.style.setProperty('pointer-events', 'auto', 'important');
              foundElements.push(selector);
            }
          });
          
          // Force body visibility
          document.body.style.setProperty('opacity', '1', 'important');
          
          console.log('[HYDRATION_GATE] CSS visibility forced', { foundElements });
        });
      });
    };
    
    // Subscribe to terminal state changes
    const unsubscribe = onAuthReadyOrBeyond((phase) => {
      triggerInitialRender(phase);
    });
    
    // Also check immediately if already in terminal state
    if (isTerminalState()) {
      const currentPhase = bootStateMachine.getPhase();
      triggerInitialRender(currentPhase);
    }
    
    return () => {
      unsubscribe?.();
    };
  }, []); // Empty deps - subscribe once on mount

  if (!isHydrated) {
    return <AppShellSkeleton />;
  }

  if (hydrationError) {
    console.warn('Hydration completed with errors:', hydrationError);
  }

  return <>{children}</>;
};